const LS_KEY = 'omens_device_fp'
let resolvedVisitorId: string | null = null

function uuid(): string {
  if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) return crypto.randomUUID()
  return 'fp-' + Math.random().toString(36).slice(2) + Date.now().toString(36)
}

function fallbackId(): string {
  if (typeof window === 'undefined') return ''
  let id = window.localStorage.getItem(LS_KEY)
  if (!id) {
    id = uuid()
    window.localStorage.setItem(LS_KEY, id)
  }
  return id
}

// Load the FingerprintJS visitorId once and cache it (memory + localStorage).
export async function loadDeviceFingerprint(): Promise<void> {
  if (typeof window === 'undefined' || resolvedVisitorId) return
  try {
    // Fingerprinting stays local; do not send package-installation telemetry.
    // Loaded on demand: the library stays out of the shared _app bundle every page downloads.
    const { default: FingerprintJS } = await import('@fingerprintjs/fingerprintjs')
    const fp = await FingerprintJS.load({ monitoring: false })
    const { visitorId } = await fp.get()
    resolvedVisitorId = visitorId
    window.localStorage.setItem(LS_KEY, visitorId)
  } catch {
    // keep the localStorage fallback
  }
}

const FIRST_INTERACTION = ['pointerdown', 'keydown', 'touchstart', 'scroll'] as const

// Computing the fingerprint takes ~150 ms of main thread (canvas, audio, fonts…), which counted as page-load
// blocking time on every page. Start it on the visitor's first interaction, when the browser is idle: forms and
// checkouts come after one, and calls made before it send the stored id (getDeviceFingerprint), as they
// already did while the fingerprint was still being computed. Returns the cleanup.
export function loadDeviceFingerprintOnFirstInteraction(): () => void {
  if (typeof window === 'undefined') return () => undefined
  const idle = window.requestIdleCallback ?? ((callback: () => void) => window.setTimeout(callback, 1))
  const stop = () => FIRST_INTERACTION.forEach((event) => window.removeEventListener(event, start))
  const start = () => {
    stop()
    idle(() => loadDeviceFingerprint())
  }
  FIRST_INTERACTION.forEach((event) => window.addEventListener(event, start, { passive: true }))
  return stop
}

// Synchronous getter for axios interceptors: prefer the resolved visitorId,
// otherwise a stable per-browser uuid so we always send SOMETHING.
export function getDeviceFingerprint(): string {
  return resolvedVisitorId || fallbackId()
}
